import type { DerivedMeasurementSnapshot, StoredSiteReading } from '~/models/measurementSession'
import { type MeasurementSiteKey, SITE_LABELS } from '~/models/measurementSite'
import type { WeightDisplayUnit } from '~/models/settings'
import type { Kilograms } from '~/types/units'
import { formatWeight } from '~/utils/format'
import {
  DerivedMassDisclaimer,
  EstimateDisclaimer,
  NAVY_ESTIMATE_LABEL,
} from './EstimateDisclaimer'
import styles from './MeasurementResult.module.css'

/**
 * The saved estimate and its derived values.
 *
 * Version 1 shows the raw estimate, measurement quality, and the disclaimer.
 * It calculates and displays NO numerical practical range (D-015), and never
 * describes the output as exact.
 */

interface MeasurementResultProps {
  derived: DerivedMeasurementSnapshot
  sites: Readonly<Partial<Record<MeasurementSiteKey, StoredSiteReading>>>
  unit: WeightDisplayUnit
}

const INVALID_REASON_TEXT: Readonly<Record<string, string>> = {
  missingHeight: 'Your height is missing, so the estimate cannot be calculated.',
  missingWaist: 'A waist-at-navel measurement is needed for the estimate.',
  missingNeck: 'A neck measurement is needed for the estimate.',
  nonFiniteInput: 'One of the measurements is not a usable number.',
  nonPositiveInput: 'Measurements must be greater than zero.',
  waistNotGreaterThanNeck:
    'The waist measurement is not larger than the neck measurement, so the estimate cannot be calculated. Check both readings.',
}

export function MeasurementResult({ derived, sites, unit }: MeasurementResultProps) {
  const flagged = (Object.entries(sites) as [MeasurementSiteKey, StoredSiteReading][]).filter(
    ([, reading]) => reading.exceedsThreshold,
  )

  return (
    <section class={styles.card} aria-labelledby="estimate-heading">
      <h2 id="estimate-heading" class={styles.heading}>
        {NAVY_ESTIMATE_LABEL}
      </h2>

      {derived.bodyFatStatus === 'invalid' || derived.bodyFatPercent === undefined ? (
        <p class={styles.unavailable} role="status">
          {INVALID_REASON_TEXT[derived.bodyFatInvalidReason ?? ''] ??
            'The estimate could not be calculated from these measurements.'}
        </p>
      ) : (
        <>
          <p class={styles.value}>{derived.bodyFatPercent.toFixed(1)}%</p>

          {derived.bodyFatStatus === 'implausible' ? (
            <p class={styles.quality} role="status">
              This result falls outside the range a body-fat percentage can take, so the
              measurements are probably mis-entered. The calculated value is shown unchanged rather
              than adjusted — check your readings.
            </p>
          ) : null}

          <EstimateDisclaimer />
        </>
      )}

      {flagged.length > 0 ? (
        <p class={styles.quality} role="status">
          {flagged.length === 1 ? 'One site varies' : `${flagged.length} sites vary`} by more than
          the allowed spread: {flagged.map(([site]) => SITE_LABELS[site]).join(', ')}. Consider
          retaking those readings.
        </p>
      ) : null}

      <dl class={styles.detail}>
        {derived.waistToHeightRatio === undefined ? null : (
          <div class={styles.row}>
            <dt>Waist-to-height ratio</dt>
            <dd>{derived.waistToHeightRatio.toFixed(3)}</dd>
          </div>
        )}
        {derived.fatMassKg === undefined ? null : (
          <div class={styles.row}>
            <dt>Estimated fat mass</dt>
            <dd>{formatWeight(derived.fatMassKg as Kilograms, unit)}</dd>
          </div>
        )}
        {derived.leanMassKg === undefined ? null : (
          <div class={styles.row}>
            <dt>Estimated lean mass</dt>
            <dd>{formatWeight(derived.leanMassKg as Kilograms, unit)}</dd>
          </div>
        )}
      </dl>

      {derived.fatMassKg === undefined ? null : <DerivedMassDisclaimer />}

      {derived.sourceWeightKg === undefined && derived.bodyFatPercent !== undefined ? (
        <p class={styles.quality}>Add a weight for this date to see estimated fat and lean mass.</p>
      ) : null}
    </section>
  )
}
